import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, ClipboardList, FileBadge, Settings } from "lucide-react";
import AssessmentCategoryBox from "./AssessmentCategoryProfileBox";
import AssessmentProfileAbout from "./AssessmentProfileAbout";
import AssessmentProfileService from "./services/AssessmentProfileService";
import { capitalizeWords } from "./utils";

const AssessmentProfile = ({ user }) => {
  const [profiles, setProfiles] = useState({});

  const getProfiles = async () => {
    const profiles = await AssessmentProfileService.getAll(user.id);
    setProfiles(profiles);
  };

  const deleteProfile = async (profileId) => {
    await AssessmentProfileService.deleteProfile(profileId);
    getProfiles();
  };
  useEffect(() => {
    getProfiles();
  }, [user]);

  return (
    <div>
      <div className="container mx-auto p-3 md:p-4 lg:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
          <h2 className="text-2xl md:text-3xl font-bold text-indigo-800">
            Assessment Profiles
          </h2>
          <Link
            to="/assessment/profile/new"
            className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2.5 px-4 rounded-md flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px] transition-colors"
          >
            <Plus size={20} />
            <span className="text-sm md:text-base">Create Profile</span>
          </Link>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-xl shadow-lg mb-6 md:mb-8 overflow-hidden hover:shadow-xl transition-all duration-300">
          <div className="px-4 py-4 md:px-6 md:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-lg md:text-xl font-bold text-white flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 md:h-6 md:w-6 mr-2 text-blue-200 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Assessment Profile Collection
              </h2>
              <p className="mt-1 text-xs md:text-sm text-white opacity-90">
                Reusable templates that define assessment structure for
                different job types
              </p>
            </div>
            <div className="w-full md:w-auto flex flex-col md:items-end">
              <div className="inline-flex items-center px-3 py-2 rounded-md text-xs md:text-sm font-medium bg-white/20 text-white border border-white/30">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                {profiles.totalProfile} Active Profiles
              </div>
              <p className="mt-2 text-xs md:text-sm text-white opacity-90">
                These profiles help standardise your assessments
              </p>
            </div>
          </div>
        </div>
        <div className="mb-6 md:mb-8">
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3 mb-6 md:mb-8 pt-4 md:pt-8 px-3 md:px-6">
              <div className="bg-white border border-gray-100 rounded-lg p-4 md:p-5 shadow-sm transition duration-200 hover:shadow-md hover:border-gray-200 hover:-translate-y-1">
                <div className="flex justify-between">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ClipboardList className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <dt className="text-xs md:text-sm font-medium text-gray-900">
                        Total Profiles
                      </dt>
                      <dd className="mt-1 text-xs text-gray-500">
                        Available assessment templates
                      </dd>
                    </div>
                  </div>
                </div>
                <div className="mt-3 md:mt-4 px-3 md:px-4 py-2 md:py-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center">
                  <span className="text-base md:text-lg font-semibold text-gray-900">
                    {profiles.totalProfile}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    profile{profiles.totalProfile > 1 ? "s" : ""}
                  </span>
                </div>
                <p className="mt-2 md:mt-3 text-xs text-gray-500 text-center">
                  {profiles.defaultProfileCount ?? 0} default profile
                </p>
              </div>
              <div className="bg-white border border-gray-100 rounded-lg p-4 md:p-5 shadow-sm transition duration-200 hover:shadow-md hover:border-gray-200 hover:-translate-y-1">
                <div className="flex justify-between">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileBadge className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <dt className="text-xs md:text-sm font-medium text-gray-900">
                        Profile Categories
                      </dt>
                      <dd className="mt-1 text-xs text-gray-500">
                        Assessment profile types
                      </dd>
                    </div>
                  </div>
                </div>
                <div className="mt-3 md:mt-4 px-3 md:px-4 py-2 md:py-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center">
                  <span className="text-base md:text-lg font-semibold text-gray-900">
                    {profiles.totalCategory}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    categorie{profiles.totalCategory > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="mt-2 md:mt-3 text-xs text-gray-500 text-center flex flex-wrap justify-center gap-1">
                  {profiles.profile &&
                    Object.entries(profiles.profile).map(
                      ([category, profile]) => (
                        <span
                          key={category}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-gray-500"
                        >
                          {profile.length} {capitalizeWords(category)}
                        </span>
                      ),
                    )}
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-lg p-4 md:p-5 shadow-sm transition duration-200 hover:shadow-md hover:border-gray-200 hover:-translate-y-1">
                <div className="flex justify-between">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Settings className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <dt className="text-xs md:text-sm font-medium text-gray-900">
                        Avg. Question Count
                      </dt>
                      <dd className="mt-1 text-xs text-gray-500">
                        Questions per assessment
                      </dd>
                    </div>
                  </div>
                </div>
                <div className="mt-3 md:mt-4 px-3 md:px-4 py-2 md:py-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center">
                  <span className="text-base md:text-lg font-semibold text-gray-900">
                    {profiles.averageQuestionCount}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">questions</span>
                </div>
                <p className="mt-2 md:mt-3 text-xs text-gray-500 text-center">
                  Average duration: {profiles.averageDuration} min
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
            {profiles.profile &&
              Object.entries(profiles.profile).map(([category, profile]) => (
                <AssessmentCategoryBox
                  key={category}
                  profiles={profile}
                  category={category}
                  onDelete={deleteProfile}
                />
              ))}
            {profiles.totalProfile === 0 ? (
              <div className="border-t border-gray-200">
                <div className="px-4 py-16 text-center">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center justify-center opacity-10">
                      <svg
                        className="h-64 w-64 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        ></path>
                      </svg>
                    </div>
                    <div className="relative">
                      <svg
                        className="mx-auto h-16 w-16 text-primary-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                        ></path>
                      </svg>
                      <h3 className="mt-4 text-lg font-medium text-gray-900">
                        No assessment profiles yet
                      </h3>
                      <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                        Assessment profiles help you create consistent
                        evaluations across similar positions. Create your first
                        profile to get started.
                      </p>
                    </div>
                  </div>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Link
                      to="/assessment/profile/new"
                      className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2.5 px-4 rounded-md flex items-center justify-center gap-2 min-h-[44px] transition-colors"
                    >
                      <Plus size={20} />
                      <span className="text-sm md:text-base">Create Profile</span>
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
        <AssessmentProfileAbout />
      </div>
    </div>
  );
};

export default AssessmentProfile;
